import { LBSeparator } from '@linearb/buzz'
import Tile from '../../custom-components/StatTile'
import DeltaIndicator from '../../custom-components/DeltaIndicator'
import { usd, compact } from '../../lib/numberFormat'
import { toneClassFor, tokenSum } from '../../utils/aiBackfillOutcome'
import { TOKEN_TILES } from '../../constants/aiTokens'
import {
  usesAdoptionKeys,
  creditsLabel,
  creditsWord
} from '../../custom-components/SensorsIcons'
import type {
  OrgAiIntegrationUsage,
  AiBackfillOutcomeSummary
} from '../../types'

// Shared before→after grid, used by both the live result drawer and the S3
// history sheet so they render an identical delta view.
interface Props {
  before?: OrgAiIntegrationUsage | null
  after?: OrgAiIntegrationUsage | null
  outcome?: AiBackfillOutcomeSummary | null
  tool?: string | null
}

const num = (n: number | undefined): string => compact.format(n ?? 0)

type TotalsKey = keyof OrgAiIntegrationUsage['totals']
type TileDef = { label: string; key: TotalsKey }

// Cost/model tools: cost + models render specially; these totals are context that
// the backfill doesn't move, shown to prove nothing else changed.
const COST_TILES: TileDef[] = [
  { label: 'Distinct users', key: 'distinct_users' },
  { label: 'User interactions', key: 'user_interactions' },
  { label: 'Code generations', key: 'code_generations' },
  { label: 'Code acceptances', key: 'code_acceptances' },
  { label: 'Lines added', key: 'lines_added' },
  { label: 'Lines removed', key: 'lines_removed' }
]

// Adoption interaction counters (Kiro / Amazon Q write these keys). Cost tools use
// COST_TILES. This selects the activity metric set only — cost/credits/models are
// shown data-driven below.
const ADOPTION_TILES: TileDef[] = [
  { label: 'Distinct users', key: 'distinct_users' },
  { label: 'Chat messages', key: 'chat_messages_sent' },
  { label: 'Inline suggestions', key: 'inline_suggestions_count' },
  { label: 'Inline acceptances', key: 'inline_acceptance_count' },
  { label: 'Lines added', key: 'lines_added' },
  { label: 'Lines removed', key: 'lines_removed' }
]

const BackfillDeltaTiles: React.FC<Props> = ({
  before,
  after,
  outcome,
  tool
}) => {
  if (!after) {
    return null
  }
  const recovered = outcome?.costRecovered ?? 0
  // Data-driven headline: a tool shows whichever of cost / credits / models it has
  // (Kiro has cost + credits, adoption-only tools like Amazon Q have neither).
  const hasCost =
    after.totals.cost_usd > 0 || (before?.totals.cost_usd ?? 0) > 0
  const hasCredits =
    after.totals.credits_used > 0 || (before?.totals.credits_used ?? 0) > 0
  const hasModels = after.models.length > 0 || (before?.models.length ?? 0) > 0
  // Most token-bearing rows carry no cost, so a backfill can recover tokens while cost
  // and models stay put; without this tile that run looks like it recovered nothing.
  const tokensAfter = tokenSum(after)
  const tokensBefore = tokenSum(before)
  const hasTokens = tokensAfter > 0 || tokensBefore > 0
  // What the backfill itself fills, as opposed to the activity counters it leaves alone.
  // Safe for adoption tools: Amazon Q reports no models at all and Kiro is caught by the
  // hasCredits branch first, so neither can land on the cost-side wording below.
  const fillsCostSide = hasCost || hasTokens || hasModels
  const tiles = usesAdoptionKeys(tool) ? ADOPTION_TILES : COST_TILES
  const totalsTile = ({ label, key }: TileDef) => (
    <Tile
      key={label}
      label={label}
      value={num(after.totals[key])}
      secondary={`was ${num(before?.totals[key])}`}
      delta={
        <DeltaIndicator
          before={before?.totals[key]}
          after={after.totals[key]}
        />
      }
    />
  )
  return (
    <div className='flex flex-col gap-4'>
      {outcome && (
        <div className='flex flex-col gap-1'>
          <span className={`text-sm font-semibold ${toneClassFor(outcome)}`}>
            {outcome.label}
          </span>
          <span className='text-xs text-muted-foreground'>
            {outcome.description}
          </span>
        </div>
      )}
      <LBSeparator />
      <div className='grid grid-cols-2 gap-2'>
        {hasCost && (
          <Tile
            label='Cost'
            value={usd.format(after.totals.cost_usd)}
            secondary={
              recovered > 0
                ? `+${usd.format(recovered)} recovered`
                : `was ${usd.format(before?.totals.cost_usd ?? 0)}`
            }
            delta={
              <DeltaIndicator
                before={before?.totals.cost_usd}
                after={after.totals.cost_usd}
              />
            }
          />
        )}
        {hasModels && (
          <Tile
            label='Models'
            value={String(after.models.length)}
            secondary={`was ${before?.models.length ?? 0}`}
            delta={
              <DeltaIndicator
                before={before?.models.length}
                after={after.models.length}
              />
            }
          />
        )}
        {hasTokens && (
          <Tile
            label='Total tokens'
            value={num(tokensAfter)}
            secondary={`was ${num(tokensBefore)}`}
            delta={
              // tokenSum(null) is 0, which would read as a "new" chip; every other tile
              // gets undefined from `before?.` and shows nothing, so match that.
              <DeltaIndicator
                before={before ? tokensBefore : null}
                after={tokensAfter}
              />
            }
          />
        )}
        {/* Which token types a provider reports varies (GitHub publishes no cache
            split), so each part shows only when it moved or has a value. */}
        {TOKEN_TILES.filter(
          ({ key }) =>
            (after.totals[key] ?? 0) > 0 || (before?.totals[key] ?? 0) > 0
        ).map(totalsTile)}
        {hasCredits && (
          <Tile
            label={creditsLabel(after.billing_unit)}
            value={num(after.totals.credits_used)}
            secondary={`was ${num(before?.totals.credits_used)}`}
            delta={
              <DeltaIndicator
                before={before?.totals.credits_used}
                after={after.totals.credits_used}
              />
            }
          />
        )}
        {tiles.map(totalsTile)}
      </div>
      <span className='text-xs text-muted-foreground'>
        {hasCredits
          ? `${hasCost ? `Cost, ${creditsWord(after.billing_unit)}` : creditsLabel(after.billing_unit)}${hasTokens ? ', tokens' : ''} and activity for this window were re-collected by the backfill.`
          : fillsCostSide
            ? 'Interactions, users and lines are unchanged by the backfill (it only fills cost, models and tokens), shown to confirm nothing else moved.'
            : 'Activity for this window was re-collected by the backfill.'}
      </span>
    </div>
  )
}

export default BackfillDeltaTiles
